import { useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { MathInputTools } from "../../Student/components/TypeDrawQuestionInput";
import HtmlWithLatex from "./HtmlWithLatex";
import DiagramHubSpoke from "./DiagramHubSpoke";
import DiagramHubSpokeOuter from "./DiagramHubSpokeOuter";
import DiagramCubeNet, { parseMdTableGrid, isCrossGrid } from "./DiagramCubeNet";
import EnglishErrorFindingLayout from "./EnglishErrorFindingLayout";
import EnglishClozePassageLayout from "./EnglishClozePassageLayout";
import {
  isErrorFindingStandalone,
  isErrorFindingContext,
  isClozeWordChoice,
} from "../utils/englishPaperDetection";
import { normalizeExtractedDisplayText } from "../utils/normalizeExtractedDisplayText";
import { resolveBlankLabelPosition, isFixedFillMarks, getFixedFillMarksTotal, getFixedMarksPerBlankLabel } from "../../../utils/fillQuestionUtils";
import { fillBlankUnderlineSx, fillBlankTextFieldSx, fillBlankTrailingLineSx, fillBlankMinWidth, fillBlankWidthCh } from "./fillBlankStyles";
import VennDiagramFill from "./VennDiagramFill";
import ImageOverlayFillInput from "../../Student/components/ImageOverlayFillInput";
import MatchingExpressionFill from "./MatchingExpressionFill";
import { buildMatchingExpressionContext } from "../utils/matchingExpression";
import FillBlankUnitTextField, { blankMathToolsAdapters } from "./FillBlankUnitTextField";

/**
 * Renders one question in paper-style layout: Qn), stem, image/description,
 * multi-fill inline answer lines (optional correct answers or input fields), and (n marks).
 * Used by Import Review and Student MockExams.
 * When inputValues + onInputChange are provided (take mode), multi-fill shows TextFields inline.
 */
// Remove trailing N-line blocks that are exact repeats of the N lines immediately before them.
// Fixes AI-generated question_text where sub-items (a)/b)) are written out twice, e.g.:
//   "Intro\na) first\nb) second\na) first\nb) second" → "Intro\na) first\nb) second"
// Applied to all question types so the fix works in Import Review AND Student pages.
const dedupeTrailingLines = (text) => {
  if (!text || text.length < 20) return text;
  const rawLines = text.split('\n');
  const significant = rawLines
    .map((t, i) => ({ t: t.trim(), i }))
    .filter(({ t }) => t.length >= 5);
  if (significant.length >= 4) {
    const normLine = (l) => l.replace(/\s+/g, ' ').toLowerCase();
    for (let n = 1; n <= Math.floor(significant.length / 2); n++) {
      const suffix    = significant.slice(-n).map(({ t }) => normLine(t));
      const preceding = significant.slice(-2 * n, -n).map(({ t }) => normLine(t));
      if (suffix.every((l, i) => l === preceding[i])) {
        const cutAt  = significant[significant.length - n].i;
        const trimmed = rawLines.slice(0, cutAt).join('\n').trimEnd();
        if (trimmed.length >= 15) return trimmed;
        break;
      }
    }
  }
  return text;
};

// Remove markdown table artifacts that represent blank answer space, e.g.:
//   |  |
//   |--|
//   |  |
// repeated many times. These are not real question content and should not render.
const stripAnswerSpaceTableArtifacts = (text) => {
  if (!text) return text;
  const lines = String(text).split('\n');
  const out = [];

  const isSingleCellTableLine = (line) => /^\s*\|\s*\|\s*$/.test(line);
  const isSingleCellSeparator = (line) => /^\s*\|\s*[-:]+\s*\|\s*$/.test(line);
  const isMarkerRule = (line) => /^\s*\{\d+\}-+\s*$/.test(line);
  // Lone hyphen artifact (1–3 hyphens, nothing else) from stripped page-break or table separator
  const isLoneHyphenArtifact = (line) => /^\s*-{1,3}\s*$/.test(line);
  // All-pipe artifact: 2+ consecutive pipe chars with no cell content (e.g. "||||||")
  const isAllPipeArtifact = (line) => /^\s*\|{2,}\s*$/.test(line);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (isLoneHyphenArtifact(line) || isAllPipeArtifact(line)) continue;

    // Detect start of a blank single-cell table artifact block.
    if (isSingleCellTableLine(line) && i + 1 < lines.length && isSingleCellSeparator(lines[i + 1])) {
      let j = i;
      let hasOnlyBlankCells = true;
      let sawSeparator = false;

      while (j < lines.length) {
        const cur = lines[j];
        if (isSingleCellSeparator(cur)) {
          sawSeparator = true;
          j++;
          continue;
        }
        if (isSingleCellTableLine(cur)) {
          j++;
          continue;
        }
        if (/^\s*$/.test(cur) || isMarkerRule(cur)) {
          j++;
          continue;
        }
        // Any other content means this is not the artifact shape.
        hasOnlyBlankCells = false;
        break;
      }

      if (sawSeparator && hasOnlyBlankCells) {
        // Skip the whole artifact block.
        i = j - 1;
        continue;
      }
    }

    out.push(line);
  }

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

// Ensure LaTeX commands like \square render by wrapping in $...$ when appropriate.
const toLatexLabel = (raw) => {
  if (!raw) return "";
  const s = String(raw).replace(/\$/g, '').trim();
  if (!s) return "";
  if (s.includes("$")) return s;
  if (/^\\[a-zA-Z]+(\s+\\[a-zA-Z]+)*$/.test(s)) {
    return `$${s}$`;
  }
  return s;
};

/**
 * Normalize a token against known correct-blank answers.
 * Handles the PDF OCR artifact where a leading "0." is dropped:
 *   "0006" → "0.0006" when correct_blanks contains "0.0006".
 * Returns the corrected string if a match is found, otherwise the original token.
 */
const normalizeChooseFromListToken = (token, correctBlanks) => {
  // Only attempt for all-digit tokens starting with 0 (e.g. "0006", "0012")
  if (!/^0\d+$/.test(token) || !Array.isArray(correctBlanks)) return token;
  const candidate = `0.${token}`;
  const candidateNum = parseFloat(candidate);
  for (const blank of correctBlanks) {
    const ansStr = Array.isArray(blank) ? blank[0] : String(blank ?? "");
    if (parseFloat(ansStr) === candidateNum) return candidate;
  }
  return token;
};

/**
 * Detect "choose from list" multi-fill format: first paragraph of question_text
 * contains only number/value tokens (no sentence wording). Returns { tokens, questionPart }
 * or null when the pattern is not found.
 */
const parseChooseFromList = (text, type) => {
  if (!['multi-fill', 'fill-in-the-blank'].includes(type)) return null;
  const cleaned = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '').trim();
  const parts = cleaned.split(/\n{2,}/);
  if (parts.length < 2) return null;
  const firstPart = parts[0].trim();
  // Bail if first block looks like a sentence
  if (firstPart.includes('?')) return null;
  // Split by 2+ spaces, tabs, pipe separators, OR individual newlines
  // (AI sometimes stores each number on its own line: "0.0006\n10\n0.002\n...")
  const tokens = firstPart.split(/\s{2,}|\t|\s*\|\s*|\n/).map(t => t.trim()).filter(Boolean);
  if (tokens.length < 2) return null;
  const isValueToken = (t) =>
    /^[-+]?\d*\.?\d+([eE][-+]?\d+)?%?$/.test(t) ||  // numbers incl. decimals, %
    /^\d+\/\d+$/.test(t);                              // fractions like 2/3
  if (!tokens.every(isValueToken)) return null;
  return { tokens, questionPart: parts.slice(1).join('\n\n').trim() };
};

/**
 * Detect arithmetic equation displayed vertically (one token per line) in fill-in-the-blank
 * question text. Pattern: lines alternate between value tokens (numbers / dots) and operators
 * (+ - × ÷ = *), optionally preceded by a prose stem on the first lines.
 *
 * Returns { stem, tokens } where tokens are the equation parts in order, or null.
 * Example input: "Fill in the missing number.\n27\n+\n30\n+\n.......\n=\n80"
 * Example output: { stem: "Fill in the missing number.", tokens: ["27","+","30","+",".......","=","80"] }
 */
const parseVerticalEquation = (text, type) => {
  if (!['fill-in-the-blank', 'multi-fill'].includes(type)) return null;
  if (!text) return null;
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 3) return null;

  const isOperator = (s) => /^[+\-×÷=/*]$/.test(s) || s === '−';
  const isValueToken = (s) =>
    /^\.{3,}$/.test(s) ||                         // dots: ......
    /^[-+]?\d[\d,. ]*$/.test(s) ||                // numbers: 27, 1,000, 3.5
    /^\d+\/\d+$/.test(s) ||                       // fractions: 3/4
    /^\\textcircled\{[^}]*\}$/.test(s) ||         // already wrapped
    /^[a-zA-Z]$/.test(s);                         // single letter variable

  // Walk lines from the end to find the equation portion (operators + values alternating)
  let equationStart = lines.length;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (isOperator(lines[i]) || isValueToken(lines[i])) {
      equationStart = i;
    } else {
      break;
    }
  }

  const equationLines = lines.slice(equationStart);
  // Need at least 3 tokens and must contain at least one operator
  if (equationLines.length < 3) return null;
  if (!equationLines.some(isOperator)) return null;
  // All equation lines must be operators or values
  if (!equationLines.every(l => isOperator(l) || isValueToken(l))) return null;
  // Must contain "=" somewhere (it's an equation, not just a list)
  if (!equationLines.includes('=')) return null;

  const stem = lines.slice(0, equationStart).join('\n');
  return { stem, tokens: equationLines };
};

/**
 * Infer the arithmetic operation for a "choose from list" question
 * so blanks can be shown inline as "...... ÷ ......".
 */
const detectListOperation = (questionText, blankLabels) => {
  const haystack = `${questionText || ''} ${(blankLabels || []).join(' ')}`.toLowerCase();
  if (/divid/.test(haystack)) return '÷';
  if (/multipl/.test(haystack)) return '×';
  if (/add|sum|\bplus\b/.test(haystack)) return '+';
  if (/subtract|minus|difference/.test(haystack)) return '−';
  return null;
};

// Strip markdown table rows that are MCQ option lists (e.g. | A: 36 cm² | B: 81 cm² | ...)
// when the question already has a structured options array. Prevents duplicate display.
// Also strips the separator row (|---|---|...) that immediately follows such a row.
const stripMCQOptionsTable = (text, options) => {
  if (!text || !Array.isArray(options) || options.length < 3) return text;
  const lines = String(text).split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*\|/.test(line)) {
      const cells = line.split('|').map(c => c.trim()).filter(Boolean);
      if (cells.length >= 3) {
        const mcqLabelCount = cells.filter(c => /^[A-E][:.)]\s*/.test(c)).length;
        if (mcqLabelCount >= 3) {
          // Skip this row and any following separator rows
          while (i + 1 < lines.length && /^\s*\|[-:|\s]+\|/.test(lines[i + 1])) i++;
          continue;
        }
      }
    }
    out.push(line);
  }
  return out.join('\n');
};

const QuestionPaperLayout = ({
  question,
  questionNumber,
  showAnswers = false,
  marks: marksProp,
  marksLabel,
  subLabel,
  inputValues,
  onInputChange,
  studentAnswers,
  hideImage = false,
  showAdminHints = false,
  showMathTools = false,
  hideBlankMarks = false,
  hideOptions = false,
  imageMaxHeight = 400,
  imageMaxWidth = "100%",
}) => {
  const blankInputRefs = useRef({});
  const [activeBlankIdx, setActiveBlankIdx] = useState(0);

  if (!question) return null;

  const interactiveFill = inputValues != null && typeof onInputChange === "function";

  const marks = (() => {
    if (marksProp != null) return marksProp;
    const t = String(question.type || '').toLowerCase().replace(/_/g, '-');
    if (t === 'multi-fill' || t === 'fill-in-the-blank' || t === 'table' || t === 'image-fill') {
      if (isFixedFillMarks(question)) {
        const fixed = getFixedFillMarksTotal(question);
        if (fixed > 0) return fixed;
      }
      const blanks = question.answer_data?.blank_marks ?? question.answer?.blank_marks;
      const blankLabelCount = (question.answer_data?.blank_labels ?? question.answer?.blank_labels ?? []).length;
      if (Array.isArray(blanks) && blanks.length > 0 && (blankLabelCount === 0 || blanks.length === blankLabelCount)) {
        const total = blanks.reduce((s, m) => s + (parseInt(m, 10) || 0), 0);
        if (total > 0) return total;
      }
      const mFromMarks = parseInt(question.marks, 10) || 0;
      const mFromDefault = parseInt(question.default_marks, 10) || 0;
      return Math.max(mFromMarks, mFromDefault) || 1;
    }
    return question.default_marks ?? question.marks ?? 1;
  })();
  const type = question.type || "short-answer";
  const tableLayout = question.answer_data?.table_layout;
  const overlayLayout = question.answer_data?.overlay_layout;
  const isImageFill = type === "image-fill";
  const isImageFillInline = isImageFill && overlayLayout?.mode === "inline";
  const isImageFillBox = isImageFill && !isImageFillInline;
  const isTable = type === "table" && Array.isArray(tableLayout?.grid) && tableLayout.grid.length > 0;
  const isVenn = isTable && tableLayout?.shape === "venn";

  // Detect English error-finding and cloze question types
  const isEnglishErrorFinding = isErrorFindingStandalone(question) || isErrorFindingContext(question);
  const isEnglishCloze = !isEnglishErrorFinding && isClozeWordChoice(question);

  // Deduplicate trailing repeated lines, then strip any leading Q-number prefix the AI echoed
  // e.g. "Q19) When this net..." → "When this net..."
  const questionText = (() => {
    const raw = normalizeExtractedDisplayText(
      dedupeTrailingLines(question.question_text || question.question || "")
    );
    const withoutOptions = stripMCQOptionsTable(raw, question.options);
    const cleaned = stripAnswerSpaceTableArtifacts(withoutOptions);
    return cleaned.replace(/^\s*Q?\d{1,2}\s*[).]\s*/i, "");
  })();

  // When rendering as a sub-question or a question with a paper_number, strip any leading
  // label prefix that the AI may have echoed in question_text (e.g. "a) " / "(a) " / "1) " / "(1) ").
  const effectiveQuestionText = (() => {
    if (!questionText) return questionText;
    // When paper_number is set, strip a matching numeric prefix (e.g. "1) ", "(1) ", "Q1. ", or bare "1\n")
    if (question.paper_number) {
      const esc = String(question.paper_number).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Try with separator: "7) " / "7. " / "(7)"
      const stripped = questionText
        .replace(new RegExp(`^\\s*(?:Q?${esc}\\s*[).]|\\(\\s*Q?${esc}\\s*\\))\\s*`, 'i'), '')
        .trim();
      if (stripped.length > 0 && stripped !== questionText) return stripped;
      // Fallback: bare number on its own line e.g. "7\nWhere had..." → strip leading "7\n"
      const strippedBare = questionText
        .replace(new RegExp(`^\\s*Q?${esc}\\s*[\\r\\n]+`, 'i'), '')
        .trim();
      if (strippedBare.length > 0 && strippedBare !== questionText) return strippedBare;
    }
    if (!subLabel) return questionText;
    const esc = subLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const stripped = questionText
      .replace(new RegExp(`^\\s*(?:${esc}\\s*[).]|\\(\\s*${esc}\\s*\\))\\s*`, 'i'), '')
      .trim();
    return stripped.length > 0 ? stripped : questionText;
  })();

  // In take mode for table-type questions, strip any embedded <table> HTML from the question
  // text so the interactive table grid (rendered below) doesn't appear alongside a static
  // duplicate table that was baked into the question_text HTML during import.
  const displayQuestionText = (() => {
    let text = (isTable && interactiveFill)
      ? effectiveQuestionText.replace(/<table[\s\S]*?<\/table>/gi, '').trim()
      : effectiveQuestionText;
    if (isImageFillBox && overlayLayout?.image_url) {
      const plain = text.replace(/<[^>]*>/g, "").trim();
      if (plain === "(Enter sub-question text)" || plain === "(Enter question text)") {
        text = "";
      }
    }
    return text;
  })();

  // Build unified image list: prefer image_urls array, fall back to image_url string
  const allImages = (() => {
    const arr = Array.isArray(question.image_urls) ? question.image_urls.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    return question.image_url ? [question.image_url] : [];
  })();

  // Image guard: only show separate image block when URL is not already in question_text (markdown or HTML)
  const hasEmbeddedImage =
    /!\[[^\]]*\]\([^)]*\)/.test(questionText) ||
    allImages.some((u) => questionText.includes(`src="${u}"`) || questionText.includes(`src='${u}'`));

  // Suppress the static image when a native interactive hub-spoke diagram will render.
  // The interactive component already shows all visual information the student needs,
  // so showing the image on top would duplicate the content.
  // Only suppress when blank_labels are present (i.e. the component CAN render);
  // if labels are missing the image is the only visual so we must keep it.
  const willRenderNativeHubSpoke = (() => {
    const dt = question.answer_data?.diagram_type;
    const labels = question.answer_data?.blank_labels;
    return (dt === 'hub-spoke-outer' || dt === 'hub-spoke') &&
           Array.isArray(labels) && labels.length > 0;
  })();

  const showImageBlock = allImages.length > 0 && !hasEmbeddedImage && !hideImage && !willRenderNativeHubSpoke && !isVenn && !isImageFillBox;
  // An interactive drawing canvas renders the figure itself (the caller passes hideImage for these),
  // so the text description is redundant beside it and is often stale AI guesswork from import.
  const hasInteractiveDiagram = !!question.answer_data?.interactive;
  // Show description when image_description is set — suppress when:
  //  a) it's a decorative stock photograph description and the image is already shown, OR
  //  b) a native hub-spoke / diagram component is rendering the information visually
  //     (willRenderNativeHubSpoke covers newly-imported records; the backend now nulls
  //     image_description on import for these questions, but old records may still have it).
  const isPhotographDescription = (desc) => {
    if (!desc) return false;
    return /^A\s+(?:close-?up\s+)?photograph\s+of\s+/i.test(desc) ||
           /^A\s+person\s+(?:reading|writing|holding|standing|sitting|looking|using|wearing|working|playing)\b/i.test(desc);
  };
  const showDescriptionBlock = question.image_description &&
    // Suppress when a native diagram component (hub-spoke etc.) already renders the content.
    !willRenderNativeHubSpoke &&
    // Suppress decorative stock-photograph descriptions when the actual image is shown.
    !(showImageBlock && isPhotographDescription(question.image_description)) &&
    // In student-facing mode, suppress the description when the image itself is already shown.
    !(showImageBlock && !showAdminHints) &&
    // Likewise when an interactive canvas is drawing the figure. Kept for admins so stale
    // descriptions stay findable (and deletable) in the question editor.
    !(hasInteractiveDiagram && !showAdminHints);

  // Cube net detection: question mentions a cube net AND image_description contains a cross-shaped table.
  // When both conditions are met, DiagramCubeNet renders the visual cross grid instead of raw text.
  const isCubeNetQuestion = (() => {
    const text = (question.question_text || "").toLowerCase();
    return /net.*cube|cube.*net|net.*fold|fold.*cube/i.test(text) ||
      (text.includes("net") && text.includes("cube"));
  })();
  const cubeNetGrid = isCubeNetQuestion
    ? parseMdTableGrid(question.image_description)
    : null;
  const showCubeNetDiagram = !!cubeNetGrid && isCrossGrid(cubeNetGrid) && !showImageBlock;

  // Multi-fill blanks: answer_data.correct_blanks or question.blanks
  const blanksFromData = question.answer_data?.correct_blanks;
  const blanksFromLegacy = question.blanks;
  const blanks =
    blanksFromData?.length > 0
      ? blanksFromData
      : blanksFromLegacy?.length > 0
        ? blanksFromLegacy.map((b) => (typeof b === "object" && b?.text != null ? [b.text] : [b]))
        : interactiveFill && inputValues?.length > 0
          ? Array.from({ length: inputValues.length }, () => [""])
          : [];
  const isMultiFill = !isTable && !isImageFillBox && (
    (["fill-in-the-blank", "multi-fill"].includes(type) && blanks.length > 0) ||
    (isImageFillInline && blanks.length > 0)
  );
  const showFillMathTools = showMathTools && interactiveFill && (isMultiFill || isTable);

  // Blank labels: expression context per blank (e.g. "½ + □ = 6")
  const blankLabels = question.answer_data?.blank_labels || [];
  const blankLabelPositions = question.answer_data?.blank_label_positions || [];

  // Per-blank marks: fixed mode shows a shared fraction (e.g. "2/2") on every blank;
  // sum mode shows each blank's own marks. Only surfaced when there are 2+ blanks so a
  // single-blank question isn't cluttered with a redundant marks chip.
  const blankMarksArr = question.answer_data?.blank_marks ?? question.answer?.blank_marks ?? [];
  const fixedFill = isFixedFillMarks(question);
  const fixedFillTotal = fixedFill ? getFixedFillMarksTotal(question) : 0;
  const perBlankMarkChip = (idx, blankCount) => {
    if (hideBlankMarks) return null;
    if (blankCount < 2) return null;
    if (fixedFill && fixedFillTotal > 0) {
      return getFixedMarksPerBlankLabel(fixedFillTotal, blankCount);
    }
    const m = parseInt(blankMarksArr[idx], 10);
    return m > 0 ? `${m} mark${m === 1 ? "" : "s"}` : null;
  };

  // "Choose from list" detection — multi-fill questions where the first paragraph of
  // question_text is a row of number tokens (e.g. "0.0006   10   0.002   12   0.001").
  const chooseFromList = parseChooseFromList(effectiveQuestionText, type);
  const matchingExpression = buildMatchingExpressionContext({
    ...question,
    question_text: effectiveQuestionText,
  });
  const listOperation = chooseFromList
    ? detectListOperation(chooseFromList.questionPart, blankLabels)
    : null;

  // Vertical arithmetic equation detection — fill-in-blank questions where the equation
  // is stored one token per line (e.g. "27\n+\n30\n+\n.......\n=\n80").
  // Only applies when no choose-from-list pattern was detected first.
  const verticalEquation = !chooseFromList
    ? parseVerticalEquation(effectiveQuestionText, type)
    : null;

  // Blank placeholder characters used in hub-spoke labels
  const BLANK_PH = ['□', '[ ]', '[]', '__', '___', '_____', '\\Box', '\\square', '\\Square'];

  // Hub-spoke diagram detection (three-tier):
  // 1. Explicit: answer_data.diagram_type === 'hub-spoke'  ← always wins, even over chooseFromList
  // 2. Semi-explicit: hub_value is present in answer_data
  // 3. Implicit: ALL blank_labels contain a blank placeholder
  //
  // MINIMUM 4 SPOKE LABELS: hub-spoke diagram only renders when there are at least
  // 4 blank labels. Questions with 2 blanks (e.g. "dividend □" / "divisor □") use
  // inline blank rendering instead.
  // GUARD: "choose from list" questions must never auto-detect as hub-spoke, but an
  // explicit diagram_type flag from the backend always takes precedence so the native
  // DiagramHubSpoke renders even when no SVG image was generated.
  // Outer two-level hub-spoke: explicit diagram_type only (no auto-detection)
  const isHubSpokeOuter = question.answer_data?.diagram_type === 'hub-spoke-outer';

  // For implicit detection, at least one label must carry meaningful expression
  // context beyond the blank placeholder itself — otherwise the rendered hub-spoke
  // boxes would all have empty blue cells (e.g. PDF answer sheet only stored "□"
  // for each blank, with no operator/operand). In that case fall through to the
  // normal multi-fill answer list.
  const hasMeaningfulLabelContext = blankLabels.some((l) => {
    let s = String(l ?? '').trim();
    for (const ph of BLANK_PH) s = s.split(ph).join('');
    return s.replace(/[\s=]/g, '').length > 0;
  });

  // Resolve the hub oval value: answer_data.hub_value is preferred (set by backend for new
  // sessions).  For older session data that lacks it, extract from the question text using
  // common patterns like "answer is always 3/8", "= 6", "equals 10", etc.
  // Computed BEFORE isHubSpoke so the implicit detection guard can use it.
  const resolvedHubValue = (() => {
    if (question.answer_data?.hub_value) return String(question.answer_data.hub_value);
    const text = String(question.question_text || '')
      .replace(/\$+/g, '')
      .replace(/\\d?frac\s*\{\s*([^}]+?)\s*\}\s*\{\s*([^}]+?)\s*\}/g, '$1/$2');
    const m =
      text.match(/\balways\s+([\d/.-]+(?:\s*\/\s*\d+)?)/i) ||
      text.match(/\bequals?\s+([\d/.-]+(?:\s*\/\s*\d+)?)/i) ||
      text.match(/=\s*([\d/.-]+(?:\s*\/\s*\d+)?)\s*(?:[.,)]|$)/);
    return m ? m[1].replace(/\s+/g, '') : undefined;
  })();

  const isHubSpoke =
    !isHubSpokeOuter && (
      question.answer_data?.diagram_type === 'hub-spoke' ||
      (!chooseFromList &&
        isMultiFill &&
        blankLabels.length >= 4 &&
        hasMeaningfulLabelContext &&
        (
          !!question.answer_data?.hub_value ||
          // Implicit: all labels have a blank placeholder AND a hub value is
          // identifiable from the question text. Without a resolved hub value the
          // diagram oval would be empty — fall through to the vertical blank list.
          (blankLabels.every((l) => BLANK_PH.some((ph) => String(l).includes(ph))) && !!resolvedHubValue)
        ))
    );

  const getBlankAnswer = (blank, idx) => {
    if (Array.isArray(blank)) return blank[0];
    if (blank && typeof blank === "object" && blank.text != null) return blank.text;
    return "";
  };

  const renderBlankField = (blankIdx) => {
    const blank = blanks[blankIdx];
    if (interactiveFill) {
      return (
        <FillBlankUnitTextField
          question={question}
          blankIdx={blankIdx}
          size="small"
          value={inputValues?.[blankIdx] ?? ""}
          onChange={(next) => onInputChange(blankIdx, next)}
          onFocus={() => setActiveBlankIdx(blankIdx)}
          inputRef={(el) => { blankInputRefs.current[blankIdx] = el; }}
          sx={{ width: "100%", ...fillBlankTextFieldSx }}
          inputProps={{ "aria-label": `Region ${blankIdx + 1}` }}
        />
      );
    }
    if (showAnswers) {
      return (
        <Box sx={{ color: "success.main", fontWeight: 700, textAlign: "center" }}>
          <HtmlWithLatex html={String(getBlankAnswer(blank, blankIdx) ?? "")} />
        </Box>
      );
    }
    return <Box sx={{ minHeight: 28, mx: 0.5, ...fillBlankUnderlineSx }} />;
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      {/* Top line: Qn) left, (X marks) right */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        <Typography
          component="span"
          variant="h6"
          sx={{ fontWeight: 600, fontSize: "1.1rem", color: subLabel ? "primary.main" : "text.primary" }}
        >
          {(() => {
            const pn = question.paper_number;
            if (pn) {
              // Sub-question with paper_number (e.g. comprehension Q1-Q5, passage Q38-Q46)
              // Strip outer parens so "(5)" becomes "5" — the sub-question wrapper already
              // provides visual context; double-parens look wrong.
              if (subLabel) {
                const s = String(pn);
                return s.replace(/^\(([^)]+)\)$/, '$1');
              }
              // Standalone: format based on what the token looks like
              const s = String(pn);
              if (/^\([^)]+\)/.test(s)) return s;          // already bracketed: (1), (a)
              if (/^Q/i.test(s))         return s;          // already has Q prefix: Q1, Q.1
              if (/^[a-z]+$/i.test(s))   return `(${s})`;  // pure letter: a → (a)
              if (/^\d+\.$/.test(s))     return `Q${s}`;   // trailing dot: 1. → Q1.
              return `Q${s})`;                               // plain number: 1 → Q1)
            }
            // No paper_number — use the fallback subLabel directly (no extra parens)
            return subLabel ? subLabel : `Q${questionNumber})`;
          })()}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.8rem", bgcolor: "grey.100", px: 0.75, py: 0.25, borderRadius: 0.5 }}
        >
          {marksLabel ? `(${marksLabel})` : `(${marks} mark${marks !== 1 ? "s" : ""})`}
        </Typography>
      </Box>

      {/* Question text (stem) */}
      {displayQuestionText && displayQuestionText.trim() ? (
        chooseFromList && !matchingExpression ? (
          /* ── "Choose from list" layout: number boxes row + question sentence ── */
          <Box sx={{ mb: 2 }}>
            {/* Paper-style bordered number boxes — touching cells like an exam table row */}
            <Box sx={{ display: "flex", flexWrap: "wrap", mb: 2, alignItems: "stretch" }}>
              {chooseFromList.tokens.map((token, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    fontFamily: "monospace",
                    minWidth: 56,
                    textAlign: "center",
                    bgcolor: "#fff",
                    color: "#1a1a1a",
                    border: "2px solid #333",
                    borderRight: i < chooseFromList.tokens.length - 1 ? "none" : "2px solid #333",
                    letterSpacing: "0.03em",
                  }}
                >
                  {normalizeChooseFromListToken(token, blanksFromData)}
                </Box>
              ))}
            </Box>
            <Box sx={{ lineHeight: 1.7, overflowWrap: "break-word", "& p": { marginBottom: 0.5 } }}>
              <HtmlWithLatex html={chooseFromList.questionPart} />
            </Box>
          </Box>
        ) : (isEnglishErrorFinding || isEnglishCloze) ? (
          /* Error-finding / cloze: EnglishXxxLayout owns all content; skip duplicate text block */
          null
        ) : verticalEquation ? (
          /* ── Vertical arithmetic equation → horizontal bubble row (e.g. [27] + [30] + [...] = [80]) ── */
          <Box sx={{ mb: 2 }}>
            {verticalEquation.stem && (
              <Box sx={{ lineHeight: 1.7, mb: 1.5, overflowWrap: "break-word", "& p": { marginBottom: 0.5 } }}>
                <HtmlWithLatex html={verticalEquation.stem} />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, my: 1 }}>
              {verticalEquation.tokens.map((token, i) => {
                const isOp = /^[+\-×÷=/*]$/.test(token) || token === '−';
                const isDots = /^\.{3,}$/.test(token);
                if (isOp) {
                  return (
                    <Box
                      key={i}
                      component="span"
                      sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#333", mx: 0.25 }}
                    >
                      {token}
                    </Box>
                  );
                }
                return (
                  <Box
                    key={i}
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDots ? "#fff" : "#5b9bd5",
                      color: isDots ? "#333" : "#fff",
                      border: isDots ? "2px solid #5b9bd5" : "none",
                      borderRadius: "50px",
                      px: 1.5,
                      py: 0.25,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      minWidth: 36,
                      letterSpacing: isDots ? "0.1em" : undefined,
                    }}
                  >
                    {token}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2, lineHeight: 1.7, wordBreak: "normal", overflowWrap: "break-word", "& p": { marginTop: 0, marginBottom: "4px" }, "& br + br": { display: "none" } }}>
            <HtmlWithLatex html={displayQuestionText} />
          </Box>
        )
      ) : question.options && question.options.length > 0 ? (
        /* Legacy questions: show options as the question when question_text is missing */
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Options
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {question.options.map((opt, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: opt.is_correct ? '#f0fdf4' : 'white',
                  borderColor: opt.is_correct ? 'success.main' : '#e0e0e0',
                }}
              >
                {opt.image_url && (
                  <Box sx={{ mb: 1 }}>
                    <img
                      src={opt.image_url}
                      alt={opt.option_text || opt.text || `Option ${idx + 1}`}
                      style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 4 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </Box>
                )}
                <Box sx={{ mb: 0.5 }}>
                  <HtmlWithLatex html={String.fromCharCode(65 + idx) + ") " + (opt.option_text || opt.text || "")} />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ) : allImages.length > 0 || isImageFillBox ? null : (
        <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1, border: "1px dashed", borderColor: "grey.400" }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Question text not available
          </Typography>
        </Box>
      )}

      {isVenn ? (
        <Box sx={{ mb: 2 }}>
          {showFillMathTools ? (
            <MathInputTools
              getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
              {...blankMathToolsAdapters(
                question,
                activeBlankIdx,
                inputValues[activeBlankIdx] ?? "",
                (next) => onInputChange(activeBlankIdx, next)
              )}
            />
          ) : null}
          <VennDiagramFill grid={tableLayout.grid} renderInput={renderBlankField} />
        </Box>
      ) : isTable ? (
        <Box sx={{ mb: 2, overflowX: "auto" }}>
          {showFillMathTools ? (
            <MathInputTools
              getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
              {...blankMathToolsAdapters(
                question,
                activeBlankIdx,
                inputValues[activeBlankIdx] ?? "",
                (next) => onInputChange(activeBlankIdx, next)
              )}
            />
          ) : null}
          {(() => {
            const colCount = Math.max(1, tableLayout.grid[0]?.length || 1);
            // Equal columns for 3–4 col tables (25% each when 4); row-label tables keep a tighter first col.
            const firstColPct = colCount >= 5 ? 22 : colCount === 4 ? 25 : null;
            const otherColPct = firstColPct != null
              ? (100 - firstColPct) / Math.max(1, colCount - 1)
              : 100 / colCount;
            const tableMaxWidth = colCount >= 5 ? 520 : colCount === 3 ? 420 : 480;
            return (
          <Box
            component="table"
            sx={{
              borderCollapse: "collapse",
              tableLayout: "fixed",
              width: "100%",
              maxWidth: tableMaxWidth,
              minWidth: Math.min(280, tableMaxWidth),
              "& td, & th": {
                border: "2px solid #333",
                px: 0.75,
                py: 0.6,
                textAlign: "center",
                verticalAlign: "middle",
                fontSize: "0.9rem",
                height: 44,
              },
              "& th": { fontWeight: 700, bgcolor: "#f5f5f5" },
              "& p": { m: 0 },
            }}
          >
            <tbody>
              {tableLayout.grid.map((row, ri) => (
                <tr key={ri}>
                  {(row || []).map((cell, ci) => {
                    const editable = cell && typeof cell.b === "number";
                    const fixedText = cell?.f ?? "";
                    const blankIdx = editable ? cell.b : -1;
                    const blank = editable ? blanks[blankIdx] : null;
                    const colWidth = `${ci === 0 && firstColPct != null ? firstColPct : otherColPct}%`;
                    return (
                      <Box
                        component="td"
                        key={ci}
                        sx={{
                          fontWeight: ri === 0 || ci === 0 ? 700 : 400,
                          bgcolor: editable ? "#fff" : "#fafafa",
                          width: colWidth,
                          minWidth: 0,
                          maxWidth: colWidth,
                        }}
                      >
                        {editable ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0.25, px: 0.25 }}>
                            {blankLabels[blankIdx] ? (
                              <Box sx={{ fontWeight: 700, lineHeight: 1.2, width: "100%", textAlign: "center" }}>
                                <HtmlWithLatex html={toLatexLabel(blankLabels[blankIdx])} />
                              </Box>
                            ) : null}
                            {interactiveFill ? (
                              <FillBlankUnitTextField
                                question={question}
                                blankIdx={blankIdx}
                                size="small"
                                value={inputValues?.[blankIdx] ?? ""}
                                onChange={(next) => onInputChange(blankIdx, next)}
                                onFocus={() => setActiveBlankIdx(blankIdx)}
                                inputRef={(el) => { blankInputRefs.current[blankIdx] = el; }}
                                sx={{
                                  width: "100%",
                                  maxWidth: "100%",
                                  ...fillBlankTextFieldSx,
                                  minWidth: 0,
                                  "& .MuiOutlinedInput-root": {
                                    ...fillBlankTextFieldSx["& .MuiOutlinedInput-root"],
                                    minWidth: 0,
                                    width: "100%",
                                  },
                                  "& .MuiInputBase-input": {
                                    textAlign: "center",
                                    px: 0.5,
                                    py: 0.5,
                                  },
                                  "& .MuiInputAdornment-root": {
                                    marginLeft: 0.25,
                                    marginRight: 0.25,
                                  },
                                  "& .MuiInputAdornment-root .MuiTypography-root": {
                                    fontSize: "0.95rem",
                                  },
                                }}
                                inputProps={{
                                  "aria-label": `Table cell row ${ri + 1} col ${ci + 1}`,
                                  style: { textAlign: "center" },
                                }}
                              />
                            ) : showAnswers ? (
                              <Box sx={{ color: "success.main", fontWeight: 700, textAlign: "center" }}>
                                <HtmlWithLatex html={String(getBlankAnswer(blank, blankIdx) ?? "")} />
                              </Box>
                            ) : (
                              <Box sx={{ minHeight: 28, width: "80%", maxWidth: 120, ...fillBlankUnderlineSx }} />
                            )}
                          </Box>
                        ) : fixedText ? (
                          <Box sx={{ px: 0.5, lineHeight: 1.25, wordBreak: "break-word" }}>
                            <HtmlWithLatex html={fixedText} />
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Box>
            );
          })()}
        </Box>
      ) : isImageFillBox ? (
        <Box sx={{ mb: 2 }}>
          {showFillMathTools ? (
            <MathInputTools
              getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
              {...blankMathToolsAdapters(
                question,
                activeBlankIdx,
                inputValues?.[activeBlankIdx] ?? "",
                (next) => onInputChange(activeBlankIdx, next)
              )}
            />
          ) : null}
          <ImageOverlayFillInput
            overlayLayout={overlayLayout}
            values={
              interactiveFill
                ? (inputValues || [])
                : Array.from(
                    { length: Math.max(overlayLayout?.blanks?.length || 0, blanks.length) },
                    () => ""
                  )
            }
            onChange={interactiveFill ? onInputChange : undefined}
            readOnly={!interactiveFill}
            prefilled={blankLabels}
          />
        </Box>
      ) : null}

      {/* ── Static image strip ────────────────────────────────────────────── */}
      {/* Suppressed for hub-spoke questions that have blank_labels (the interactive
          component already renders all the information the student needs).
          Shown as a fallback when blank_labels are missing or for all other types. */}
      {showImageBlock && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            bgcolor: "grey.50",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {allImages.map((url, idx) => (
            <Box
              key={idx}
              component="img"
              src={url}
              alt={allImages.length > 1 ? `Image ${idx + 1} for this question` : "Diagram for this question"}
              sx={{
                width: "auto",
                maxWidth: imageMaxWidth,
                maxHeight: imageMaxHeight,
                objectFit: "contain",
                borderRadius: 1,
                border: "1px solid #ddd",
                display: "block",
              }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ))}
        </Box>
      )}

      {isHubSpokeOuter && blankLabels.length > 0 ? (
        <DiagramHubSpokeOuter
          blankLabels={blankLabels}
          hubValue={resolvedHubValue}
          inputValues={inputValues}
          onInputChange={onInputChange}
          showAnswers={showAnswers || showAdminHints}
          correctBlanks={blanks}
          studentAnswers={studentAnswers}
          showMathTools={showFillMathTools}
        />
      ) : isHubSpoke && blankLabels.length > 0 ? (
        <DiagramHubSpoke
          blankLabels={blankLabels}
          hubValue={resolvedHubValue}
          inputValues={inputValues}
          onInputChange={onInputChange}
          showAnswers={showAnswers || showAdminHints}
          correctBlanks={blanks}
          studentAnswers={studentAnswers}
          showMathTools={showFillMathTools}
        />
      ) : (
        <>
          {/* Diagram description - cube net gets a visual cross grid; others show text */}
          {showDescriptionBlock && (
            showCubeNetDiagram ? (
              /* ── Cube net: render cross-shaped visual grid ── */
              <DiagramCubeNet imageDescription={question.image_description} />
            ) : (
              <Box
                sx={{
                  mb: 2,
                  border: "2px solid",
                  borderColor: showImageBlock ? "warning.300" : "warning.main",
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: showImageBlock ? "warning.50" : "warning.50",
                  boxShadow: !showImageBlock ? "0 2px 8px rgba(255,152,0,0.2)" : "none",
                }}
              >
                {/* Header bar - prominent "Diagram" when image missing */}
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    bgcolor: showImageBlock ? "warning.100" : "warning.light",
                    borderBottom: "1px dashed",
                    borderColor: showImageBlock ? "warning.200" : "warning.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "warning.900", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {!showImageBlock ? "Diagram (text description — no image)" : "Image"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {showImageBlock
                      ? "(description for reference)"
                      : showAdminHints
                        ? "(image not extracted from PDF — use \u201cGenerate Diagram\u201d or Edit \u2192 \u201cAdd Image\u201d to attach one)"
                        : "(refer to this description)"}
                  </Typography>
                </Box>
                {/* Description body */}
                <Box sx={{ p: 1.5 }}>
                  <HtmlWithLatex html={question.image_description} />
                  {/* If blank_labels exist, show each expression in a grid */}
                  {blankLabels.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {blankLabels.map((label, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            bgcolor: "white",
                            border: "1px solid",
                            borderColor: "primary.300",
                            borderRadius: 1,
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: "primary.800",
                          }}
                        >
                          <HtmlWithLatex html={toLatexLabel(label)} />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )
          )}

          {/* Multi-fill: inline answer lines (paper style) — review: show answers; take: show TextFields */}
          {isMultiFill && (
            <>
            {showFillMathTools ? (
              <MathInputTools
                getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
                {...blankMathToolsAdapters(
                  question,
                  activeBlankIdx,
                  inputValues[activeBlankIdx] ?? "",
                  (next) => onInputChange(activeBlankIdx, next)
                )}
              />
            ) : null}
            {matchingExpression && interactiveFill ? (
              <MatchingExpressionFill
                context={matchingExpression}
                values={Array.isArray(inputValues) ? inputValues : []}
                onChange={onInputChange}
              />
            ) : chooseFromList ? (
              /* ── "Choose from list" inline blanks: dividend [___] ÷ divisor [___] ── */
              <Box sx={{ mt: 2, mb: 2 }}>
                {/* Expression row: label + blank [+ operator + label + blank ...] */}
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
                  {blanks.map((blank, idx) => {
                    const chooseWidthCh = fillBlankWidthCh(question, idx, getBlankAnswer(blank, idx));
                    const chooseWidth = chooseWidthCh ? `${chooseWidthCh}ch` : Math.max(130, fillBlankMinWidth);
                    return (
                    <Box key={idx} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                      {/* Label (dividend / divisor / etc.) above or before the blank */}
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
                        {blankLabels[idx] && (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.75rem", lineHeight: 1 }}
                          >
                            {blankLabels[idx]}
                          </Typography>
                        )}
                        {/* Blank input or dashed line */}
                        {inputValues != null && typeof onInputChange === "function" ? (
                          <FillBlankUnitTextField
                            question={question}
                            blankIdx={idx}
                            size="small"
                            placeholder="......"
                            value={inputValues[idx] ?? ""}
                            onChange={(next) => onInputChange(idx, next)}
                            onFocus={() => setActiveBlankIdx(idx)}
                            inputRef={(el) => { blankInputRefs.current[idx] = el; }}
                            sx={{
                              width: chooseWidth,
                              ...fillBlankTextFieldSx,
                            }}
                            inputProps={{ "aria-label": blankLabels[idx] || `Blank ${idx + 1}` }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: chooseWidth,
                              minHeight: 28,
                              display: "flex",
                              alignItems: "center",
                              pl: 0.5,
                              ...fillBlankUnderlineSx,
                            }}
                          >
                            {showAnswers && (
                              <Typography variant="body2" sx={{ color: "success.main", fontWeight: 700 }}>
                                {getBlankAnswer(blank, idx)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      {/* Operation symbol between blanks */}
                      {listOperation && idx < blanks.length - 1 && (
                        <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "text.primary", pb: 0.25 }}>
                          {listOperation}
                        </Typography>
                      )}
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                {blanks.map((blank, idx) => {
                  const labelPos = resolveBlankLabelPosition(blankLabels[idx], blankLabelPositions, idx);
                  const hasLabel = Boolean(blankLabels[idx]);
                  const labelIsExpression = labelPos === "before" && hasLabel;
                  const blankWidthCh = fillBlankWidthCh(question, idx, getBlankAnswer(blank, idx));
                  const defaultCap = labelPos === "after" ? 200 : 280;

                  const blankInput = inputValues != null && typeof onInputChange === "function" ? (
                    <FillBlankUnitTextField
                      question={question}
                      blankIdx={idx}
                      size="small"
                      placeholder="..."
                      value={inputValues[idx] ?? ""}
                      onChange={(next) => onInputChange(idx, next)}
                      onFocus={() => setActiveBlankIdx(idx)}
                      inputRef={(el) => { blankInputRefs.current[idx] = el; }}
                      sx={{
                        flex: blankWidthCh ? "0 0 auto" : (labelPos === "after" ? "0 0 auto" : "1 1 140px"),
                        width: blankWidthCh ? `${blankWidthCh}ch` : (labelPos === "after" ? fillBlankMinWidth : undefined),
                        minWidth: fillBlankMinWidth,
                        maxWidth: blankWidthCh ? "100%" : defaultCap,
                        ...fillBlankTextFieldSx,
                      }}
                      inputProps={{ "aria-label": blankLabels[idx] || `Blank ${idx + 1}` }}
                    />
                  ) : (
                    <Box
                      sx={{
                        flex: blankWidthCh ? "0 0 auto" : (labelPos === "after" ? "0 0 auto" : "1 1 120px"),
                        width: blankWidthCh ? `${blankWidthCh}ch` : (labelPos === "after" ? fillBlankMinWidth : undefined),
                        minWidth: fillBlankMinWidth,
                        maxWidth: blankWidthCh ? "100%" : defaultCap,
                        minHeight: 28,
                        alignSelf: "flex-end",
                        ...fillBlankUnderlineSx,
                      }}
                    />
                  );

                  const suffixLabel = hasLabel && labelPos === "after" ? (
                    <Box
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "0.9rem",
                        flexShrink: 0,
                      }}
                    >
                      <HtmlWithLatex html={toLatexLabel(blankLabels[idx])} />
                    </Box>
                  ) : null;

                  const prefixLabel = labelIsExpression ? (
                    <Box
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "0.9rem",
                        maxWidth: "100%",
                        wordBreak: "normal",
                        overflowWrap: "break-word",
                      }}
                    >
                      <HtmlWithLatex html={toLatexLabel(blankLabels[idx].replace("□", "_____"))} />
                    </Box>
                  ) : !hasLabel ? (
                    <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                      {idx + 1}.
                    </Typography>
                  ) : null;

                  return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {prefixLabel}
                    {blankInput}
                    {suffixLabel}
                    {labelPos === "after" && !interactiveFill && (
                      <Box sx={fillBlankTrailingLineSx} />
                    )}
                    {perBlankMarkChip(idx, blanks.length) && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: 700, fontSize: "0.7rem", bgcolor: "grey.100", px: 0.6, py: 0.2, borderRadius: 0.5, flexShrink: 0 }}
                      >
                        {perBlankMarkChip(idx, blanks.length)}
                      </Typography>
                    )}
                    {showAnswers && !(inputValues != null && onInputChange) && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: "success.main", fontWeight: 600, flexShrink: 0 }}
                      >
                        Answer: {getBlankAnswer(blank, idx)}
                      </Typography>
                    )}
                  </Box>
                  );
                })}
              </Box>
            )}
            </>
          )}
        </>
      )}

      {/* Options: always rendered for MCQ when options exist; correct/incorrect only when showAnswers.
          Suppressed via hideOptions when an interactive answer input renders the options separately
          (e.g. grouped sub-questions on the take page), to avoid showing a static duplicate. */}
      {!hideOptions && question.options && question.options.length > 0 && [
        "multiple-choice",
        "multiple-select",
        "true-false",
        "ordering",
        "matching",
      ].includes(type) && (
        isEnglishErrorFinding ? (
          showAnswers ? (
            <Box sx={{ mt: 1 }}>
              <EnglishErrorFindingLayout
                question={question}
                showAnswers={showAnswers}
                studentAnswer={studentAnswers}
                mode={studentAnswers != null ? "review" : "preview"}
              />
            </Box>
          ) : null
        ) : isEnglishCloze ? (
          showAnswers ? (
            <Box sx={{ mt: 1 }}>
              <EnglishClozePassageLayout
                question={question}
                showAnswers={showAnswers}
                studentAnswer={studentAnswers}
                mode={studentAnswers != null ? "review" : "preview"}
              />
            </Box>
          ) : null
        ) : (
          <Box sx={{ mt: 2 }}>
            {(type === "matching" || type === "ordering") && (
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {type === "matching" ? "Matching pairs" : "Ordering items"}
              </Typography>
            )}
            <Box
              sx={(() => {
                const hasImageOptions = question.options.some((o) => o.image_url);
                const isMcqType = ["multiple-choice", "multiple-select", "true-false"].includes(type);
                return {
                  display: (isMcqType && hasImageOptions) ? "grid" : "flex",
                  flexDirection: (isMcqType && hasImageOptions) ? undefined : "column",
                  gridTemplateColumns: (isMcqType && hasImageOptions) ? "repeat(auto-fill, minmax(140px, 1fr))" : undefined,
                  gap: 1,
                };
              })()}
            >
              {question.options.map((opt, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    borderColor: showAnswers && opt.is_correct ? "success.main" : "#e0e0e0",
                    borderWidth: showAnswers && opt.is_correct ? 2 : 1,
                    bgcolor: showAnswers && opt.is_correct ? "#f0fdf4" : "white",
                  }}
                >
                  {opt.image_url && (
                    <Box sx={{ mb: 0.5 }}>
                      <img
                        src={opt.image_url}
                        alt={opt.option_text || opt.text || `Option ${idx + 1}`}
                        style={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </Box>
                  )}
                  {["multiple-choice", "multiple-select", "true-false"].includes(type) && (
                    <Box sx={{ display: "block", color: "text.secondary", fontSize: "0.8rem" }}>
                      <strong>{String.fromCharCode(65 + idx)}:</strong>{" "}
                      <HtmlWithLatex html={opt.option_text || opt.text || ""} />
                    </Box>
                  )}
                  {type === "ordering" && (
                    <Box>
                      <Typography variant="body2" component="span"><strong>Order {idx + 1}:</strong> </Typography>
                      <HtmlWithLatex html={opt.option_text || opt.text || ""} />
                    </Box>
                  )}
                  {type === "matching" && (
                    <Box>
                      <Typography variant="body2" component="span"><strong><HtmlWithLatex html={opt.option_text || opt.text || ""} /></strong></Typography>
                      {opt.match_text != null && opt.match_text !== "" ? (
                        <Typography variant="body2" component="span"> → <HtmlWithLatex html={opt.match_text} /></Typography>
                      ) : null}
                    </Box>
                  )}
                  {showAnswers && ["multiple-choice", "multiple-select", "true-false"].includes(type) && (
                    <Typography variant="caption" color={opt.is_correct ? "success.main" : "text.secondary"} sx={{ fontWeight: 600, display: "block", mt: 0.25 }}>
                      {opt.is_correct ? "✓ Correct" : "Incorrect"}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        )
      )}
    </Box>
  );
};

export default QuestionPaperLayout;
